import { OptionsButtonBuilder } from '@/model/DeviceAdminModel';
import { HelpFeature } from '@/model/HelpModel';
import { InstructionsDarkIcon, OperatorNoteDarkIcon } from '@/resources/Icons';
import { MinusSquareTwoTone, PlusSquareTwoTone, SettingFilled } from '@ant-design/icons';
import { Badge, Button, Checkbox, Empty, Space, Spin } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { ColumnsProps, buildColumn, getColumnWidth, handleResponse, parseDateTimeString, useWindowResize } from '../../../Helper';
import { alarmApi } from '../../../api';
import constants from '../../../constants';
import { SubPermissions, User, getPermissionErrorMessage } from '../../../model/AccountModel';
import {
	AcknowledgedAndClearAlarmsPayload,
	AlarmAcknowledgementActions,
	AlarmActionOptions,
	AlarmColorType,
	AlarmDetail,
	AlarmDetailStatus,
	AlarmDetailsExpandedState,
	AlarmListResponseObject,
	AlarmModel,
	AlarmNote,
	AlarmNoteForAcknowledge,
	EventPaginationSetting,
	EventSortDirection,
} from '../../../model/AlarmModel';
import { CheckboxMenuOptions, GridDataColumnItem, ResponseStatusCode, SelectOptions, TupleTyped } from '../../../model/CommonModel';
import { Logger } from '../../../model/LoggingModel';
import { useStoreDispatch, useStoreSelector } from '../../../store';
import {
	setAlarmNoteForAcknowledge,
	setOperatorNotesAdded,
	setShowAlarmAddOperatorNote,
	setShowAlarmCredentials,
	setShowAlarmDetails,
} from '../../../store/alarm/actions';
import { selectAlarmColors, selectOperatorNotesAdded } from '../../../store/alarm/selectors';
import {
	selectAckAlarmList,
	selectAlarmList,
	selectClearedAlarmList,
	selectSecuredAlarmList,
	selectTotalAcknowledgedAlarms,
	selectTotalActiveAlarms,
} from '../../../store/common/selectors';
import { AlarmActionDropdown, AlarmViewerProperties } from '../../alarms';
import { HeaderBar, HelpFactoryComponent } from '../../common';
import { IndividualActionButtons } from '../IndividualActionButtons/IndividualActionButtons';
import { AlarmExpandColumn, columnKeys, getColumnValue } from '../VirtualTableExpandableRow/ExpandableRow';
import { VirtualTableExpandableRow } from '../VirtualTableExpandableRow/VirtualTableExpandableRow';
import {
	AcknowledgedAlarmTableColumns,
	AlarmActionColumnData,
	AlarmDataPayload,
	AlarmTableColumns,
	AlarmTableDataSourceModel,
	acknowledgeAndClearAlarmCommon,
	acknowledgedAndClearAlarms,
	addNotes,
	createTableData,
	getAlarmBackgroundColor,
	getAlarmCount,
	getAlarmNoteAddOperatorNoteModal,
	getAlarmTextColor,
	getCurrentPage,
	getMultipleAlarmAction,
	instructionsStyle,
	notesStyle,
	selectAllAlarms,
	twoToneIconColors,
	unsupportedColumnKeys,
	updateTotalAlarmPages,
} from '../helpers';
import styles from './alarmTable.module.scss';

const user: User = getUser();
// !queueSortDir OLDEST TO MOST RECENT - ASC
// queueSortDir MOST RECENT TO OLDEST  - DESC
const { alarmsPerPage, alwaysShowInstructions, alarmStackingEnabled, queueSortDir: ifTrueUseSortDescending } = user;
const canChangeColumnConfiguration: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmViewer_ChangeProperties).allowed;
const canCopyRows: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmViewer_CopytoClipboard).allowed;
const noteIconWidth = 20;
const instructionIconWidth = 22;
enum AlarmTableContextAction {
	CopyRows = 0,
	CopyRowsWithHeaders,
	DisplayCredential,
	CopyAddress,
}

enum SetDataSourceReason {
	SocketResponse,
	ChangeCheckbox,
	ExpandRow,
	GetStoredAlarms,
	AddedNote,
}

type IconViewColumn = {
	notes: boolean;
	instruction: boolean;
	expand: boolean;
	notesAndInstructions: boolean;
};

const createContextMenuOptions = (): OptionsButtonBuilder<number> => {
	return {
		labelOrIcon: '...',
		options: [
			{
				id: 'alarmContextMenuOptions-CopyRows',
				key: `copyRowsToClipboard-${AlarmTableContextAction.CopyRows}`,
				label: _('CopyRows'),
				disabled: !canCopyRows,
				value: AlarmTableContextAction.CopyRows,
				title: getPermissionErrorMessage(canCopyRows),
			},
			{
				id: 'alarmContextMenuOptions-CopyRowsWithHeaders',
				key: `copyRowsToClipboard-${AlarmTableContextAction.CopyRowsWithHeaders}`,
				label: _('CopyRowsWithHeaders'),
				disabled: !canCopyRows,
				value: AlarmTableContextAction.CopyRowsWithHeaders,
				title: getPermissionErrorMessage(canCopyRows),
			},
		],
	};
};

type Props = {
	isAcknowledgedAlarmTable: boolean;
	topicId?: string;
};

const defaultPaginationSettings: EventPaginationSetting = {
	PageNumber: 1,
	PageSize: alarmsPerPage,
	SortDirection: EventSortDirection.OperatorPreference,
	SortField: '',
};
const defaultIconViewColumn: IconViewColumn = { expand: false, instruction: false, notes: false, notesAndInstructions: false };
type AlarmTableColumnsUnion = keyof AlarmTableColumns | keyof AcknowledgedAlarmTableColumns;

const component: React.FC<Props> = ({ isAcknowledgedAlarmTable, topicId = '' }) => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [tableColumns, setTableColumns] = useState<ColumnsProps<AlarmTableDataSourceModel>[]>([]);
	const [dataSource, setDataSource] = useState<AlarmTableDataSourceModel[]>([]);
	const [selectedAlarmKeys, setSelectedAlarmKeys] = useState<React.Key[]>([]);
	const [totalAlarms, setTotalAlarms] = useState<number>(-1);
	const [showAlarmConfigureColumns, setShowAlarmConfigureColumns] = useState<boolean>(false);
	const [totalAlarmPages, setTotalAlarmPages] = useState<number>(0);
	const [paginationSettings, setPaginationSettings] = useState<EventPaginationSetting>(defaultPaginationSettings);
	const [maxHeight, setMaxHeight] = useState(270);
	const alarmWithCredentials = useRef<number>(0);
	const checkboxMenuChecked = useRef<boolean>(false);
	const firstRender = useRef<boolean>(true);
	const iconViewColumnReference = useRef<IconViewColumn>(defaultIconViewColumn);
	const dataSourceReference = useRef<AlarmTableDataSourceModel[]>(dataSource);
	const setDataSourceReference = (newDataSourceReference: AlarmTableDataSourceModel[], reason: SetDataSourceReason): void => {
		if (reason !== SetDataSourceReason.ChangeCheckbox) {
			let alarmIndex: number = -1;
			let {
				expand: hasExpandIcon,
				instruction: hasInstructions,
				notes: hasNotes,
				notesAndInstructions: hasDoubleIcon,
			}: IconViewColumn = defaultIconViewColumn;

			newDataSourceReference.every((alarm, index) => {
				if (!hasExpandIcon) {
					hasExpandIcon = (alarm.AlarmDetails.length > 1 && alarmStackingEnabled) || alarm.IsExpanded;
				}
				if (alarm.Instructions !== '') {
					hasInstructions = true;
					alarmIndex = index;
				}
				if (alarm.AlarmDetails?.length > 0 && alarm.AlarmDetails[0].AlarmNotes.length > 0) {
					hasNotes = true;
					if (alarmIndex === index) {
						hasDoubleIcon = true;
					}
				}

				alarmIndex = -1;
				if (hasDoubleIcon && (!alarmStackingEnabled || (alarmStackingEnabled && hasExpandIcon))) {
					return false;
				}

				return true;
			});

			iconViewColumnReference.current = {
				notes: hasNotes,
				instruction: hasInstructions,
				expand: hasExpandIcon,
				notesAndInstructions: iconViewColumnReference.current.notesAndInstructions,
			};
			if (hasDoubleIcon !== iconViewColumnReference.current.notesAndInstructions) {
				iconViewColumnReference.current.notesAndInstructions = hasDoubleIcon;
			}
		}

		dataSourceReference.current = newDataSourceReference;
		setDataSource(newDataSourceReference);
	};
	const getDataSourceReference = (): AlarmTableDataSourceModel[] => {
		return dataSourceReference.current;
	};
	const selectedAlarmKeysReference = useRef<React.Key[]>(selectedAlarmKeys);
	const setSelectedAlarmKeysReference = (newSelectedAlarmKeysReference: React.Key[]): void => {
		selectedAlarmKeysReference.current = newSelectedAlarmKeysReference;
		setSelectedAlarmKeys(newSelectedAlarmKeysReference);
	};
	const getSelectedAlarmKeysReference = (): React.Key[] => {
		return selectedAlarmKeysReference.current;
	};

	const realTimeAlarmList: AlarmModel[] = isAcknowledgedAlarmTable ? [] : useStoreSelector<AlarmModel[]>(selectAlarmList);
	const realTimeAckAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectAckAlarmList);
	const realTimeClearedAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectClearedAlarmList);
	const realTimeSecuredAlarmList: number[] = useStoreSelector<number[]>(selectSecuredAlarmList);
	const operatorNotesAdded: AlarmNote[] = useStoreSelector(selectOperatorNotesAdded);
	const totalActiveAlarms: number = useStoreSelector<number>(isAcknowledgedAlarmTable ? selectTotalAcknowledgedAlarms : selectTotalActiveAlarms);
	const alarmColors: Record<AlarmColorType, string> = useStoreSelector<Record<AlarmColorType, string>>(selectAlarmColors);
	//#endregion
	const sessionStorageFetchKey: string = isAcknowledgedAlarmTable
		? constants.sessionStorage.alarmViewer.FETCH_ACK_TABLE
		: constants.sessionStorage.alarmViewer.FETCH_ACTIVE_TABLE;

	const sessionStorageLiveEventKey: string = isAcknowledgedAlarmTable
		? constants.sessionStorage.alarmViewer.LIVE_ACK_TABLE
		: constants.sessionStorage.alarmViewer.LIVE_ACTIVE_TABLE;

	const prefix: string = isAcknowledgedAlarmTable ? 'Ack' : 'Alarm';
	const hasInstructionIcon = !alwaysShowInstructions && iconViewColumnReference.current.instruction;
	const instructionWidth = hasInstructionIcon ? instructionIconWidth : 0;
	let actionWidth = 0;
	if (iconViewColumnReference.current.notesAndInstructions) {
		actionWidth = instructionWidth + noteIconWidth;
	} else if (iconViewColumnReference.current.notes) {
		actionWidth = noteIconWidth;
	} else if (hasInstructionIcon) {
		actionWidth = instructionWidth;
	}

	const tableColumnsWidth: Record<AlarmTableColumnsUnion, string> = isAcknowledgedAlarmTable
		? ({
				CheckColumn: '35px',
				CollapseIcon: iconViewColumnReference.current.expand ? '35px' : '0px',
				Action: `${actionWidth}px`,
				Icon: '25px',
				HardwareTime: '13.2em',
				HostTime: '13.2em',
				AcknowledgedTime: '13.2em',
				Workstation: '10em',
				Description: '45em',
				Address: '15em',
				AcknowledgedBy: '11em',
				Level: '5em',
				ActiveAlarmId: '7em',
				ActionsColumn: '8.5em',
		  } as Record<AlarmTableColumnsUnion, string>)
		: ({
				CheckColumn: '35px',
				CollapseIcon: iconViewColumnReference.current.expand ? '35px' : '0px',
				Action: `${actionWidth}px`,
				Icon: '25px',
				HardwareTime: '13.2em',
				HostTime: '13.2em',
				Description: '45em',
				Address: '15em',
				Level: '5em',
				ActiveAlarmId: '7em',
				ActionsColumn: '8.5em',
		  } as Record<AlarmTableColumnsUnion, string>);

	const [width, height] = useWindowResize();
	useEffect(() => {
		if (height === 1080) {
			setMaxHeight(270);
		} else if (height > 1700) {
			setMaxHeight(650);
		} else if (height > 1080) {
			setMaxHeight(400);
		}
	}, [height]);

	useEffect(() => {
		//We use this flag to avoid multiple api calls at the same time
		window.sessionStorage.setItem(sessionStorageLiveEventKey, '0');
		window.sessionStorage.setItem(sessionStorageFetchKey, '0');

		if (alarmsPerPage) {
			initTable();
		}
	}, []);

	useEffect(() => {
		if (operatorNotesAdded?.length) {
			setDataSourceReference(addNotes(getDataSourceReference(), operatorNotesAdded), SetDataSourceReason.AddedNote);
			dispatch(setOperatorNotesAdded([]));
		}
	}, [operatorNotesAdded]);

	useEffect(() => {
		if (!firstRender.current) {
			initTable();
		} else {
			firstRender.current = false;
		}
	}, [alarmColors]);

	useEffect(() => {
		if (realTimeAlarmList.length && !isAcknowledgedAlarmTable) {
			addDataFromSocket(realTimeAlarmList, [], []);
		}
	}, [realTimeAlarmList]);

	useEffect(() => {
		if (realTimeAckAlarmList.length) {
			if (isAcknowledgedAlarmTable) {
				addDataFromSocket(realTimeAckAlarmList, [], []);
			} else {
				addDataFromSocket([], realTimeAckAlarmList, []);
			}
		}
	}, [realTimeAckAlarmList]);

	useEffect(() => {
		if (realTimeSecuredAlarmList.length) {
			addDataFromSocket([], [], realTimeSecuredAlarmList);
		}
	}, [realTimeSecuredAlarmList]);

	useEffect(() => {
		if (realTimeClearedAlarmList.length) {
			addDataFromSocket([], realTimeClearedAlarmList, []);
		}
	}, [realTimeClearedAlarmList]);

	const addDataFromSocket = (addedItems: AlarmModel[], removeItems: AlarmModel[], securedAlarmList: number[]) => {
		const dataSourceModified = addedItems.length || removeItems.length || securedAlarmList.length;
		if (dataSourceModified) {
			window.sessionStorage.setItem(sessionStorageLiveEventKey, '1');
			const actualDataSource = getDataSourceReference();
			const dataSourceCount: number = getAlarmCount(actualDataSource);
			let newDataSource: AlarmTableDataSourceModel[] = undefined;
			if (addedItems.length) {
				newDataSource = addData({
					source: actualDataSource,
					newAlarms: addedItems,
					removeAlarms: removeItems,
					paginationSettings,
					isRealTimeAlarms: true,
					securedAlarmList: securedAlarmList,
				});
			} else if (securedAlarmList.length) {
				updateAlarmSecureStatus(actualDataSource, securedAlarmList);
			}
			newDataSource = newDataSource ?? actualDataSource;
			if (removeItems.length) {
				newDataSource = removeAlarmFromTable(removeItems, newDataSource);
			}
			if (alarmStackingEnabled) {
				newDataSource = getExpandedRows(newDataSource);
			}

			const newDataSourceCount: number = getAlarmCount(newDataSource);
			setDataSourceReference(createTableData(newDataSource), SetDataSourceReason.SocketResponse);
			removeSelectedKeyAlarm(removeItems, getSelectedAlarmKeysReference());
			if (newDataSourceCount === 0) {
				window.sessionStorage.setItem(sessionStorageLiveEventKey, '0');
				loadFirstPage();
			} else {
				if (newDataSourceCount !== dataSourceCount) {
					const newPage = getCurrentPage(newDataSourceCount, alarmsPerPage);
					const currentPage = newPage === 0 ? 1 : newPage;
					const totalPages = updateTotalAlarmPages(currentPage, totalAlarmPages);
					setTotalAlarmPages(totalPages);
					setTotalAlarms(newDataSourceCount);
				}
				window.sessionStorage.setItem(sessionStorageLiveEventKey, '0');
			}
		}
	};

	const removeAlarmFromTable = (alarmsToRemove: AlarmModel[], sourceTarget: AlarmTableDataSourceModel[]): AlarmTableDataSourceModel[] => {
		let clonedDataSource: AlarmTableDataSourceModel[] = [...sourceTarget];
		let isInHeaderDetails = false;
		alarmsToRemove.forEach(alarmModelToRemove => {
			alarmModelToRemove.AlarmDetails.forEach(alarmToRemove => {
				let fatherHeaderAlarmDetails: AlarmDetail[] = [];
				clonedDataSource.forEach((currentAlarm, index, alarmDataSources) => {
					if (currentAlarm.HeaderAlarmDetails && currentAlarm.HeaderAlarmDetails.find(alarm => alarm.ActiveAlarmId === alarmToRemove.ActiveAlarmId)) {
						currentAlarm.HeaderAlarmDetails = currentAlarm.HeaderAlarmDetails.filter(x => x.ActiveAlarmId !== alarmToRemove.ActiveAlarmId);
						currentAlarm.HeaderCount = currentAlarm.HeaderAlarmDetails.length;
						fatherHeaderAlarmDetails = currentAlarm.HeaderAlarmDetails;
						if (currentAlarm.HeaderAlarmDetails.length <= 1) {
							currentAlarm = { ...currentAlarm, HeaderAlarmDetails: [], IsExpanded: false, ExpandColumn: AlarmExpandColumn.None };
						}
						isInHeaderDetails = true;
						alarmDataSources[index] = currentAlarm;
					}

					let alarmDetail: AlarmDetail = currentAlarm.AlarmDetails.find(alarm => alarm.ActiveAlarmId === alarmToRemove.ActiveAlarmId);
					if (alarmDetail) {
						const newAlarmDetails = currentAlarm.AlarmDetails.filter(x => x.ActiveAlarmId !== alarmToRemove.ActiveAlarmId);
						if (alarmDetail.IsChecked) {
							setAlarmWithCredentials(alarmDetail, false);
						}
						if (newAlarmDetails.length === 0) {
							if (isInHeaderDetails && currentAlarm.HeaderAlarmDetails.length > 0) {
								const nextIndex = index + 1;
								if (alarmDataSources.length > nextIndex + 1) {
									if (currentAlarm.HeaderAlarmDetails.length > 1) {
										alarmDataSources[nextIndex].HeaderAlarmDetails = currentAlarm.HeaderAlarmDetails;
									}
									alarmDataSources[nextIndex].IsExpanded = currentAlarm.IsExpanded;
									alarmDataSources[nextIndex].ExpandColumn = currentAlarm.ExpandColumn;
								}
							}
							alarmDataSources.splice(index, 1);
						} else {
							currentAlarm.AlarmDetails = [...newAlarmDetails];
							alarmDataSources[index] = { ...currentAlarm, IsExpanded: false, ExpandColumn: AlarmExpandColumn.None };
						}
					}

					if (
						currentAlarm.hasOwnProperty('HeaderAlarmDetails') &&
						currentAlarm.HeaderAlarmDetails?.length === 0 &&
						fatherHeaderAlarmDetails.length > 0 &&
						currentAlarm.AlarmDetails &&
						currentAlarm.AlarmDetails.length > 0
					) {
						const alarmIndex = fatherHeaderAlarmDetails.findIndex(x => x.ActiveAlarmId === currentAlarm.AlarmDetails[0].ActiveAlarmId);
						if (
							(fatherHeaderAlarmDetails.length > 0 && alarmIndex === fatherHeaderAlarmDetails.length - 1) ||
							fatherHeaderAlarmDetails.length === 1
						) {
							alarmDataSources[index].ExpandColumn =
								fatherHeaderAlarmDetails.length === 1 ? AlarmExpandColumn.None : AlarmExpandColumn.LastExpandedRow;
							fatherHeaderAlarmDetails = [];
						}
					}
					isInHeaderDetails = false;
				});
			});
		});

		return clonedDataSource;
	};

	const removeSelectedKeyAlarm = (alarms: AlarmModel[], selectedAlarmKeys: React.Key[]) => {
		const alarmIds = selectAllAlarms(alarms);
		const newSelectedKeys: React.Key[] = selectedAlarmKeys.filter(key => !alarmIds.includes(key.toString()));
		setSelectedAlarmKeysReference(newSelectedKeys);
	};

	const loadFirstPage = () => {
		fetchData({
			...paginationSettings,
			PageNumber: 1,
		});
	};

	const toggleItemActive = (itemIndex: number) => {
		const prevState = getDataSourceReference();
		let newExpandedValue = false;
		let newAlarm: AlarmTableDataSourceModel;
		let result = prevState.map((alarm, index) => {
			if (index === itemIndex) {
				newExpandedValue = !alarm.IsExpanded;
				newAlarm = { ...alarm, IsExpanded: !alarm.IsExpanded };
				if (alarm.IsExpanded) {
					let alarmDetails: AlarmDetail[] = prevState.filter(x => isSameAlarmToStack(x, alarm)).map(x => x.AlarmDetails[0]);
					newAlarm.AlarmDetails.push(...alarmDetails);
					newAlarm.AlarmDetails = getUniqueAlarmRows(newAlarm.AlarmDetails);
					newAlarm.HeaderAlarmDetails = [];
				}

				return newAlarm;
			}
			return alarm;
		});

		if (!newExpandedValue && newAlarm) {
			result = result.filter((x, index) => {
				const isSameAlarmRow: boolean = isSameAlarmToStack(x, newAlarm);
				return x.IsExpanded || !isSameAlarmRow || (isSameAlarmRow && index === itemIndex);
			});
		} else {
			result = getExpandedRows(result);
		}
		setDataSourceReference(result, SetDataSourceReason.ExpandRow);
	};

	const setSelectedAlarm = (alarmId: string, checked: boolean) => {
		const prevState = getSelectedAlarmKeysReference();
		let alarmKeys: React.Key[] = [...prevState];
		if (checked) {
			if (!prevState.includes(alarmId)) {
				alarmKeys = [...prevState, alarmId];
			}
		} else {
			alarmKeys = prevState.filter(key => key !== alarmId);
		}

		setSelectedAlarmKeysReference(alarmKeys);
	};

	const setAlarmWithCredentials = (alarmDetail: AlarmDetail, checked) => {
		alarmWithCredentials.current = 0;
		if (alarmDetail.UID1 > 0 || alarmDetail.UID2 > 0) {
			if (alarmDetail.IsChecked && !checked) {
				alarmWithCredentials.current--;
			} else if (checked && !alarmDetail.IsChecked) {
				alarmWithCredentials.current++;
			}
		}
	};

	const onChangeAlarmCheckbox = (
		checked: boolean,
		activeAlarmId: number,
		isExpandableRowHeader: boolean,
		isExpanded: boolean,
		unselectUnrelated: boolean = false,
		isRightClick: boolean = false
	) => {
		if (isRightClick) {
			setSelectedAlarmKeysReference([]);
		}
		const prevState = getDataSourceReference();
		setDataSourceReference(
			prevState.map(alarm => {
				if (alarm.HeaderAlarmDetails && alarm.HeaderAlarmDetails.length > 0) {
					const hasAlarmId = alarm.HeaderAlarmDetails.some(x => x.ActiveAlarmId === activeAlarmId);
					if (hasAlarmId) {
						const alarmHeaderDetails = alarm.HeaderAlarmDetails.map(alarmHeaderDetail => {
							if (isExpandableRowHeader && !isExpanded) {
								setSelectedAlarm(alarmHeaderDetail.ActiveAlarmId.toString(), checked);
								return {
									...alarmHeaderDetail,
									IsChecked: checked,
								};
							} else if (alarmHeaderDetail.ActiveAlarmId === activeAlarmId) {
								setSelectedAlarm(alarmHeaderDetail.ActiveAlarmId.toString(), checked);
								return {
									...alarmHeaderDetail,
									IsChecked: checked,
								};
							}
							return alarmHeaderDetail;
						});
						alarm.HeaderAlarmDetails = [...alarmHeaderDetails];
					}
				}

				const hasAlarmId = alarm.AlarmDetails.some(x => x.ActiveAlarmId === activeAlarmId);
				if (hasAlarmId) {
					const alarmDetails = alarm.AlarmDetails.map(alarmDetail => {
						if (isExpandableRowHeader && !isExpanded) {
							setSelectedAlarm(alarmDetail.ActiveAlarmId.toString(), checked);
							setAlarmWithCredentials(alarmDetail, checked);
							return {
								...alarmDetail,
								IsChecked: checked,
							};
						} else if (alarmDetail.ActiveAlarmId === activeAlarmId) {
							setSelectedAlarm(alarmDetail.ActiveAlarmId.toString(), checked);
							setAlarmWithCredentials(alarmDetail, checked);
							return {
								...alarmDetail,
								IsChecked: checked,
							};
						}
						return alarmDetail;
					});
					return { ...alarm, AlarmDetails: [...alarmDetails] };
				}

				if (unselectUnrelated) {
					return {
						...alarm,
						AlarmDetails: alarm.AlarmDetails.map(detail => {
							return {
								...detail,
								IsChecked: false,
							};
						}),
					};
				}

				return alarm;
			}),
			SetDataSourceReason.ChangeCheckbox
		);
	};

	const handleOnChangeAlarmCheckbox = (event: CheckboxChangeEvent, alarm: AlarmActionColumnData) => {
		const { checked } = event.target;
		const { ActiveAlarmId, IsExpandableRowHeader, IsExpanded } = alarm;
		onChangeAlarmCheckbox(checked, ActiveAlarmId, IsExpandableRowHeader, IsExpanded);
	};

	const getIndeterminateHeaderCheckboxMenu = (alarm: AlarmActionColumnData): boolean => {
		const { dataSource } = alarm;

		const quantityOfAlarms = dataSource.reduce((quantity, alarm) => alarm.AlarmDetails.length + quantity, 0);
		const quantityOfCheckedAlarms = dataSource.reduce((quantity, alarm) => {
			return quantity + alarm.AlarmDetails.filter(x => x.IsChecked).length;
		}, 0);

		if (!quantityOfCheckedAlarms) {
			checkboxMenuChecked.current = false;
		} else {
			checkboxMenuChecked.current = true;
		}

		return quantityOfAlarms !== quantityOfCheckedAlarms && quantityOfCheckedAlarms >= 1;
	};

	const getIndeterminateRowCheckbox = (alarm: AlarmActionColumnData): boolean => {
		const { IsExpandableRowHeader, AlarmDetails, IsExpanded } = alarm;

		if (!IsExpandableRowHeader || (IsExpandableRowHeader && IsExpanded)) {
			return false;
		}

		const quantityOfAlarms = AlarmDetails.length;
		const quantityOfCheckedAlarms = AlarmDetails.filter(alarm => alarm.IsChecked).length;

		return quantityOfAlarms !== quantityOfCheckedAlarms && quantityOfCheckedAlarms >= 1;
	};

	const handleOnChangeCheckboxMenu = (checked: boolean) => {
		checkboxMenuChecked.current = checked;
		const prevState = getDataSourceReference();
		if (checked) {
			selectStateAllAlarms(prevState);
		} else {
			setSelectedAlarmKeysReference([]);
		}
		setDataSourceReference(
			prevState.map(alarm => {
				if (alarm.HeaderAlarmDetails && alarm.HeaderAlarmDetails.length > 0) {
					alarm.HeaderAlarmDetails = alarm.HeaderAlarmDetails.map(alarmHeaderDetail => {
						return {
							...alarmHeaderDetail,
							IsChecked: checked,
						};
					});
				}

				const alarmDetails = alarm.AlarmDetails.map(alarmDetail => {
					setAlarmWithCredentials(alarmDetail, checked);
					return {
						...alarmDetail,
						IsChecked: checked,
					};
				});
				return { ...alarm, AlarmDetails: [...alarmDetails] };
			}),
			SetDataSourceReason.ChangeCheckbox
		);
	};

	const selectStateAllAlarms = (dataSource: AlarmTableDataSourceModel[]) => {
		const newKeys = selectAllAlarms(dataSource);
		setSelectedAlarmKeysReference([...newKeys]);
	};

	const handleCheckboxMenuAction = (option: CheckboxMenuOptions) => {
		switch (option) {
			case CheckboxMenuOptions.SELECT_ALL:
				handleOnChangeCheckboxMenu(true);
				break;
			case CheckboxMenuOptions.CLEAR_ALL:
				handleOnChangeCheckboxMenu(false);
				break;
		}
	};

	const getRowAction = (alarm: AlarmActionColumnData, alarmModel: AlarmTableDataSourceModel): React.ReactNode => {
		const alarmNotes: AlarmNote[] = alarm.AlarmNotes;
		const firstAlarmDetail: AlarmDetail = alarmModel.AlarmDetails?.length > 0 ? alarmModel.AlarmDetails[0] : undefined;
		const instructionsData: AlarmModel = alarmModel.HeaderAlarmDetails?.length > 0 ? { ...alarmModel, AlarmDetails: [firstAlarmDetail] } : alarmModel;
		const alarmNoteModel: AlarmDetail[] = alarmModel.AlarmDetails?.length > 0 ? [alarmModel.AlarmDetails[0]] : [];

		return (
			<div className={styles.rowActions}>
				{!alwaysShowInstructions && alarm.Instructions !== '' ? (
					<span
						onClick={() =>
							dispatch(
								setShowAlarmDetails({
									alarmModel: instructionsData,
									showAlarmDetailsExpandedState: AlarmDetailsExpandedState.Instructions,
								})
							)
						}>
						<InstructionsDarkIcon style={{ ...instructionsStyle, color: '#ee9c4e' }} />
					</span>
				) : (
					<span />
				)}
				{alarmNotes && alarmNotes.length > 0 ? (
					<span
						onClick={() =>
							dispatch(
								setShowAlarmDetails({
									alarmModel: { ...alarmModel, AlarmDetails: alarmNoteModel },
									showAlarmDetailsExpandedState: AlarmDetailsExpandedState.Notes,
								})
							)
						}>
						<OperatorNoteDarkIcon style={{ ...notesStyle, color: '#677DB7', fontSize: 20 }} />
					</span>
				) : (
					<span />
				)}
			</div>
		);
	};

	const getHeaderAction = (alarm: AlarmActionColumnData): React.ReactNode => {
		const isIndeterminate = getIndeterminateHeaderCheckboxMenu(alarm);
		const extraProps = isIndeterminate ? { indeterminate: isIndeterminate } : {};

		return (
			<Checkbox
				id={`checkboxHeaderActionColumn${prefix}`}
				aria-label={`checkboxHeaderActionColumn${prefix}`}
				checked={checkboxMenuChecked.current}
				onChange={(e: CheckboxChangeEvent) => {
					const { checked } = e.target;
					handleOnChangeCheckboxMenu(checked);
				}}
				{...extraProps}
			/>
		);
	};

	const createActionElement = (alarm: AlarmActionColumnData, row: AlarmTableDataSourceModel): React.ReactNode => {
		const { columnActionHeader } = alarm;
		return <>{!columnActionHeader ? getRowAction(alarm, row) : null}</>;
	};

	const isSameAlarmToStack = (alarmA: AlarmTableDataSourceModel, alarmB: AlarmTableDataSourceModel): boolean =>
		alarmA.Description?.trim() === alarmB.Description?.trim() && alarmA.Address?.trim() === alarmB.Address?.trim() && alarmA.EventId === alarmB.EventId;

	const getStackedAlarms = (clonedDataSource: AlarmTableDataSourceModel[], alarmsToStack: AlarmTableDataSourceModel[]): AlarmTableDataSourceModel[] => {
		alarmsToStack.forEach(alarm => {
			const stackedAlarmIndex: number[] = clonedDataSource.reduce((previousValue: number[], x: AlarmTableDataSourceModel, index: number) => {
				if (isSameAlarmToStack(x, alarm)) {
					previousValue.push(index);
				}

				return previousValue;
			}, []);

			if (stackedAlarmIndex.length === 1) {
				const alarmIndex: number = stackedAlarmIndex.shift();
				clonedDataSource[alarmIndex].NeedsSorting = true;
				clonedDataSource[alarmIndex].AlarmDetails.push(...alarm.AlarmDetails);
			} else if (stackedAlarmIndex.length > 1) {
				const firstIndex: number = stackedAlarmIndex.shift();
				if (clonedDataSource[firstIndex].HeaderAlarmDetails && clonedDataSource[firstIndex].HeaderAlarmDetails.length > 0) {
					clonedDataSource[firstIndex].NeedsSorting = true;
					clonedDataSource[firstIndex].HeaderAlarmDetails.push(...alarm.AlarmDetails);
				}
			} else {
				alarm.NeedsSorting = true;
				clonedDataSource.push(alarm);
			}
		});

		return clonedDataSource;
	};

	const addData = ({
		source,
		newAlarms,
		removeAlarms,
		paginationSettings,
		isRealTimeAlarms,
		securedAlarmList,
	}: AlarmDataPayload): AlarmTableDataSourceModel[] => {
		const updatedAlarmList: AlarmTableDataSourceModel[] = [];
		const sourceKeys = selectAllAlarms(source);
		newAlarms.forEach((x: AlarmModel) => {
			let header: AlarmModel = undefined;
			x.AlarmDetails.forEach((y: AlarmDetail) => {
				if (!(removeAlarms && removeAlarms.find(z => z.AlarmDetails[0].ActiveAlarmId === y.ActiveAlarmId))) {
					if (!sourceKeys.includes(y.ActiveAlarmId.toString())) {
						if (header === undefined) {
							header = { ...x, AlarmDetails: [] };
						}
						header.AlarmDetails.push({ ...y });
					}
				}
			});

			if (header !== undefined) {
				updatedAlarmList.push(header);
			}
		});

		if (securedAlarmList.length === 0 && updatedAlarmList.length === 0) {
			return source;
		}

		let needsGlobalSort: boolean = true;
		let updatedDataSource: AlarmTableDataSourceModel[];
		if (updatedAlarmList.length !== 0) {
			if (alarmStackingEnabled) {
				// first we stacked the new alarms between each other to optimize the amount of loops
				const clonedDataSource = getStackedAlarms(source, updatedAlarmList);
				clonedDataSource.forEach(x => {
					if (x.NeedsSorting) {
						x.NeedsSorting = false;
						if (x.HeaderAlarmDetails && x.HeaderAlarmDetails.length > 0) {
							const sortResult = sortAlarmDetail(getUniqueAlarmRows(x.HeaderAlarmDetails), paginationSettings);
							x.HeaderAlarmDetails = sortResult.Item2;
							if (sortResult.Item1) {
								const [firstItem] = x.HeaderAlarmDetails;
								x.AlarmDetails = [{ ...firstItem }];
							}
						} else {
							const sortResult = sortAlarmDetail(getUniqueAlarmRows(x.AlarmDetails), paginationSettings);
							x.AlarmDetails = sortResult.Item2;
						}
					}
				});
				updatedDataSource = clonedDataSource;
			} else {
				let sortField: string;
				let sortDirectionDescending: boolean;
				if (paginationSettings.SortField) {
					sortField = paginationSettings.SortField;
					sortDirectionDescending = paginationSettings.SortDirection === EventSortDirection.Descending;
				} else {
					sortField = isAcknowledgedAlarmTable ? 'AcknowledgedTime' : 'HostTime';
					sortDirectionDescending = ifTrueUseSortDescending;
				}

				if (isRealTimeAlarms) {
					if ((!isAcknowledgedAlarmTable && sortField === 'HostTime') || (isAcknowledgedAlarmTable && sortField === 'AcknowledgedTime')) {
						if (sortDirectionDescending) {
							//We reverse the list since we have the events from the socket are sorted newest to oldest
							updatedDataSource = [...updatedAlarmList.reverse(), ...source];
						} else {
							updatedDataSource = [...source, ...updatedAlarmList];
						}
						needsGlobalSort = false;
					} else {
						updatedDataSource = [...source, ...updatedAlarmList];
					}
				} else {
					updatedDataSource = [...source, ...updatedAlarmList];
					needsGlobalSort = false;
				}
			}
		} else {
			updatedDataSource = source;
		}

		updateAlarmSecureStatus(updatedDataSource, securedAlarmList);
		if (needsGlobalSort) {
			sortAlarmTableDataSourceModel(updatedDataSource, paginationSettings);
		}

		return updatedDataSource;
	};

	const updateAlarmSecureStatus = (updatedDataSource: AlarmTableDataSourceModel[], securedAlarms: number[]) => {
		securedAlarms.forEach(relatedId => {
			let alarmIndex = updatedDataSource.findIndex(z => z.AlarmDetails.some(a => a.ActiveAlarmId === relatedId));
			if (alarmIndex >= 0) {
				const alarmDetailIndex = updatedDataSource[alarmIndex].AlarmDetails.findIndex(a => a.ActiveAlarmId === relatedId);
				updatedDataSource[alarmIndex].AlarmDetails[alarmDetailIndex].Status = AlarmDetailStatus.Secured;
			}

			alarmIndex = updatedDataSource.findIndex(z => z.HeaderAlarmDetails?.some(a => a.ActiveAlarmId === relatedId));
			if (alarmIndex >= 0) {
				const alarmDetailIndex = updatedDataSource[alarmIndex].HeaderAlarmDetails.findIndex(a => a.ActiveAlarmId === relatedId);
				updatedDataSource[alarmIndex].HeaderAlarmDetails[alarmDetailIndex].Status = AlarmDetailStatus.Secured;
			}
		});
	};

	// We will return the array since we don't longer have the same reference in the useRef object
	const sortAlarmDetail = (alarmDetails: AlarmDetail[], paginationSettings: Partial<EventPaginationSetting>): TupleTyped<boolean, AlarmDetail[]> => {
		let sortField: string;
		let sortDirectionDescending: boolean;
		if (paginationSettings.SortField) {
			sortField = paginationSettings.SortField;
			sortDirectionDescending = paginationSettings.SortDirection === EventSortDirection.Descending;
		} else {
			sortField = isAcknowledgedAlarmTable ? 'AcknowledgedTime' : 'HostTime';
			sortDirectionDescending = ifTrueUseSortDescending;
		}

		const gotSorted = currentPreferenceNeedsSortForAlarmDetails(sortField);
		if (!gotSorted) {
			return { Item1: false, Item2: alarmDetails };
		}

		alarmDetails.sort((a, b) => {
			let val1: string | React.ReactNode | AlarmExpandColumn | Date | AlarmActionColumnData = null,
				val2: string | React.ReactNode | AlarmExpandColumn | Date | AlarmActionColumnData = null;
			if (sortDirectionDescending) {
				val1 = getColumnValue({
					column: sortField,
					alarmDetail: b,
				});
				val2 = getColumnValue({
					column: sortField,
					alarmDetail: a,
				});
			} else {
				val1 = getColumnValue({
					column: sortField,
					alarmDetail: a,
				});
				val2 = getColumnValue({
					column: sortField,
					alarmDetail: b,
				});
			}

			// Date comparison
			if (['HostTime', 'HardwareTime', 'AcknowledgedTime'].includes(sortField)) {
				return new Date(val1 as Date).getTime() - new Date(val2 as Date).getTime();
			}
			// String comparison
			else if (['Workstation', 'Description', 'Address', 'AcknowledgedBy'].includes(sortField)) {
				return val1.toString().localeCompare(val2.toString());
			}
		});

		return { Item1: true, Item2: alarmDetails };
	};

	const sortAlarmTableDataSourceModel = (alarmDetails: AlarmTableDataSourceModel[], paginationSettings: Partial<EventPaginationSetting>) => {
		let sortField: string;
		let sortDirectionDescending: boolean;
		if (paginationSettings.SortField) {
			sortField = paginationSettings.SortField;
			sortDirectionDescending = paginationSettings.SortDirection === EventSortDirection.Descending;
		} else {
			sortField = isAcknowledgedAlarmTable ? 'AcknowledgedTime' : 'HostTime';
			sortDirectionDescending = ifTrueUseSortDescending;
		}

		alarmDetails.sort((a, b) => {
			let val1: string | React.ReactNode | AlarmExpandColumn | Date | AlarmActionColumnData = null,
				val2: string | React.ReactNode | AlarmExpandColumn | Date | AlarmActionColumnData = null;
			if (sortDirectionDescending) {
				val1 = getColumnValue({
					column: sortField,
					alarmDetail: b.AlarmDetails?.[0],
					data: b,
				});
				val2 = getColumnValue({
					column: sortField,
					alarmDetail: a.AlarmDetails?.[0],
					data: a,
				});
			} else {
				val1 = getColumnValue({
					column: sortField,
					alarmDetail: a.AlarmDetails?.[0],
					data: a,
				});
				val2 = getColumnValue({
					column: sortField,
					alarmDetail: b.AlarmDetails?.[0],
					data: b,
				});
			}

			// Date comparison
			if (['HostTime', 'HardwareTime', 'AcknowledgedTime'].includes(sortField)) {
				return new Date(val1 as Date).getTime() - new Date(val2 as Date).getTime();
			}
			// String comparison
			else if (['Workstation', 'Description', 'Address', 'AcknowledgedBy'].includes(sortField)) {
				return val1.toString().localeCompare(val2.toString());
			} else if (['Level', 'ActiveAlarmId'].includes(sortField)) {
				return Number(val1) - Number(val2);
			}
		});
	};

	const currentPreferenceNeedsSortForAlarmDetails = (sortField: string): boolean => {
		return ['HostTime', 'HardwareTime', 'Workstation', 'AcknowledgedBy', 'AcknowledgedTime'].includes(sortField);
	};

	const getExpandedRows = (dataSource: AlarmTableDataSourceModel[]): AlarmTableDataSourceModel[] => {
		let expandedRows: AlarmTableDataSourceModel[] = [];
		let clonedState: AlarmTableDataSourceModel[] = [...dataSource];

		dataSource.forEach((x, alarmIndex) => {
			if (x.IsExpanded) {
				clonedState = clonedState.filter((y, index) => {
					const isSameAlarmRow: boolean = isSameAlarmToStack(x, y);
					return y.IsExpanded || !isSameAlarmRow || (isSameAlarmRow && index === alarmIndex);
				});
			}
		});

		clonedState.forEach(x => {
			if (x.IsExpanded) {
				if (x.HeaderAlarmDetails && x.HeaderAlarmDetails.length > 0) {
					x.HeaderAlarmDetails.push(...x.AlarmDetails.filter(y => y.ActiveAlarmId !== x.AlarmDetails[0].ActiveAlarmId));
					let alarmsToAdd: AlarmDetail[] = x.HeaderAlarmDetails.length > 0 ? x.HeaderAlarmDetails : x.AlarmDetails;
					expandedRows.push(...addExpandedRow(x, x.HeaderAlarmDetails, alarmsToAdd));
				} else {
					let alarmsToAdd: AlarmDetail[] = x.HeaderAlarmDetails?.length > 0 ? x.HeaderAlarmDetails : x.AlarmDetails;
					expandedRows.push(...addExpandedRow(x, x.AlarmDetails, alarmsToAdd));
				}
			} else {
				expandedRows.push(x);
			}
		});

		const activeAlarmIds: number[] = [];
		expandedRows.forEach(
			x =>
				(x.AlarmDetails = x.AlarmDetails.filter(y => {
					if (!activeAlarmIds.includes(y.ActiveAlarmId)) {
						activeAlarmIds.push(y.ActiveAlarmId);
						return true;
					}
					return false;
				}))
		);
		expandedRows = expandedRows.filter(x => x.AlarmDetails.length > 0);

		return expandedRows;
	};

	const addExpandedRow = (
		alarmBase: AlarmTableDataSourceModel,
		alarmDetailsBaseArray: AlarmDetail[],
		alarmDetailsAddArray: AlarmDetail[]
	): AlarmTableDataSourceModel[] => {
		const expandedRows: AlarmTableDataSourceModel[] = [];
		alarmDetailsBaseArray.forEach((y, index) => {
			const isFirstRow: boolean = index === 0;

			expandedRows.push({
				...alarmBase,
				IsExpanded: isFirstRow,
				Count: isFirstRow ? alarmDetailsBaseArray.length : 1,
				HeaderCount: isFirstRow ? alarmDetailsAddArray.length : 1,
				HeaderAlarmDetails: isFirstRow ? alarmDetailsAddArray : [],
				AlarmDetails: [y],
				ExpandColumn: index === alarmDetailsBaseArray.length - 1 ? AlarmExpandColumn.LastExpandedRow : AlarmExpandColumn.InExpandedRow,
			});
		});

		return expandedRows;
	};

	const getUniqueAlarmRows = (alarmRows: AlarmDetail[]): AlarmDetail[] =>
		alarmRows.filter(
			(alarmDetail: AlarmDetail, index: number, self: AlarmDetail[]) => index === self.findIndex(x => x.ActiveAlarmId === alarmDetail.ActiveAlarmId)
		);

	const fetchData = (paginationSettings: EventPaginationSetting): void => {
		if (window.sessionStorage.getItem(sessionStorageLiveEventKey) === '1' || window.sessionStorage.getItem(sessionStorageFetchKey) === '1') {
			return;
		}
		window.sessionStorage.setItem(sessionStorageFetchKey, '1');
		setIsLoading(true);
		getStoredAlarms(
			{
				...paginationSettings,
			},
			isAcknowledgedAlarmTable,
			selectAllAlarms(getDataSourceReference()),
			false
		)
			.then(res => {
				processGetStoredAlarmResponse(res, getDataSourceReference(), totalAlarms, paginationSettings);
			})
			.catch(err => {
				Logger.writeErrorLog(err);
			})
			.finally(() => {
				setIsLoading(false);
				window.sessionStorage.setItem(sessionStorageFetchKey, '0');
			});
	};

	const processGetStoredAlarmResponse = (
		response: AlarmListResponseObject,
		source: AlarmTableDataSourceModel[],
		totalAlarms: number,
		paginationSettings: EventPaginationSetting
	) => {
		const { alarmList, totalRecords } = response;
		if (totalRecords !== totalAlarms) {
			let updatedState: AlarmTableDataSourceModel[] = addData({
				source,
				newAlarms: alarmList,
				paginationSettings,
				isRealTimeAlarms: false,
				securedAlarmList: [],
			});
			if (alarmStackingEnabled) {
				updatedState = getExpandedRows(updatedState);
			}
			const newDataSourceCount: number = getAlarmCount(updatedState);
			const newPage = getCurrentPage(newDataSourceCount, alarmsPerPage);
			const currentPage = newPage === 0 ? 1 : newPage;
			const totalPages = updateTotalAlarmPages(currentPage, totalAlarmPages);
			setTotalAlarmPages(totalPages);
			setTotalAlarms(newDataSourceCount);
			setDataSourceReference(createTableData(updatedState), SetDataSourceReason.GetStoredAlarms);
		}
	};

	const initTable = async (): Promise<void> => {
		alarmApi.getAlarmsTableColumns(isAcknowledgedAlarmTable).then(tableColumnsResponse => {
			if (tableColumnsResponse.ResponseStatusCode === ResponseStatusCode.Success) {
				const tableColumns = tableColumnsResponse.Entity;
				const columns = tableColumns
					.filter(x => !x.IsAuxColumn && !unsupportedColumnKeys.some(c => x.DataIndex === c))
					.map<ColumnsProps<AlarmTableDataSourceModel>>((c: GridDataColumnItem) => {
						return {
							...buildColumn(c.Value, c.DataIndex, undefined, c.DataIndex === columnKeys.Icon ? 'center' : 'start'),
							key: c.Key,
							dataIndex: c.DataIndex,
							sorter: !c.DisableSort,
							'data-header': c.Value,
							render: (columnData, row) => {
								let text = columnData;
								if (c.IsDateFormat) {
									text = parseDateTimeString(columnData, false, columnData, true);
								}

								return <span style={getAlarmTextColor(row.ReturnedToNormal, alarmColors)}>{text}</span>;
							},
						};
					});

				const actionsColumn: ColumnsProps<AlarmTableDataSourceModel>[] = [
					{
						...buildColumn('', 'CheckColumn', undefined, 'start'),
						key: 'CheckColumnHeader',
						dataIndex: 'CheckColumn',
						render: (columnData: AlarmActionColumnData, alarm: AlarmTableDataSourceModel) => {
							const { columnActionHeader } = columnData;
							if (columnActionHeader) {
								return getHeaderAction(columnData);
							} else {
								const checkboxId: string = `checkboxRowActionColumn${prefix}-${alarm.ActiveAlarmId}`;
								const isIndeterminate: boolean = getIndeterminateRowCheckbox(columnData);
								const extraProps = isIndeterminate ? { indeterminate: isIndeterminate } : {};
								return (
									<Checkbox
										id={checkboxId}
										aria-label={`checkboxRowActionColumn${prefix}`}
										checked={alarm.AlarmDetails[0]?.IsChecked}
										onChange={(e: CheckboxChangeEvent) => handleOnChangeAlarmCheckbox(e, columnData)}
										{...extraProps}>
										<label htmlFor={checkboxId} className={styles.srOnly}>{`${_('SelectItem')}`}</label>
									</Checkbox>
								);
							}
						},
					},
					{
						...buildColumn('', 'CollapseIcon', undefined, 'start'),
						key: 'CollapseIconHeader',
						dataIndex: 'CollapseIcon',
						render: (expandIcon: AlarmExpandColumn, row: AlarmTableDataSourceModel) => {
							let component: JSX.Element = <></>;
							switch (expandIcon) {
								case AlarmExpandColumn.ExpandIcon:
									component = <MinusSquareTwoTone twoToneColor={twoToneIconColors} />;
									break;

								case AlarmExpandColumn.CollapseIcon:
									component = <PlusSquareTwoTone twoToneColor={twoToneIconColors} />;
									if (alarmStackingEnabled && row.AlarmDetails?.length > 1) {
										component = (
											<Badge
												color='#CBCBCB'
												count={row.AlarmDetails.length}
												overflowCount={99}
												size='small'
												style={{
													fontSize: 10,
													padding: '0 4px',
													minHeight: 12,
													minWidth: 12,
													transform: 'translate(82%, -48%)',
													color: 'black',
												}}>
												{component}
											</Badge>
										);
									}
									break;

								case AlarmExpandColumn.InExpandedRow:
									component = <span className={styles.inExpandColumn}></span>;
									break;

								case AlarmExpandColumn.LastExpandedRow:
									component = <span className={styles.lastExpandColumn}></span>;
									break;
							}
							return component;
						},
					},
					{
						...buildColumn('', 'Action', undefined, 'start'),
						key: 'ActionHeader',
						dataIndex: 'Action',
						render: (value: AlarmActionColumnData, row: AlarmTableDataSourceModel) => createActionElement(value, row),
					},
				];

				const actionColumn: ColumnsProps<AlarmTableDataSourceModel> = {
					...buildColumn(_('Actions'), 'ActionsColumn', undefined, 'center'),
					key: 'ActionsColumnHeader',
					dataIndex: 'ActionsColumn',
					className: styles.actionColumn,
					render: (_: any, record: AlarmTableDataSourceModel) => {
						return (
							<IndividualActionButtons
								handleAction={async (option: AlarmAcknowledgementActions | AlarmActionOptions) => {
									handleActionDropdown(option, record);
								}}
								isAcknowledgedAlarm={isAcknowledgedAlarmTable}
								isAlarmSelected={true}
								onlyIcons
								disableAllButtons={selectedAlarmKeysReference.current?.length > 0}
							/>
						);
					},
				};

				setTableColumns([...actionsColumn, ...columns, actionColumn]);
				loadFirstPage();
			}
		});
	};

	const getStoredAlarms = async (
		paginationSettings: EventPaginationSetting,
		acknowledged: boolean,
		excludeAlarmIds: string[],
		isForAlarmSound?: boolean
	): Promise<AlarmListResponseObject> => {
		return alarmApi.getStoredAlarms({ ...paginationSettings }, acknowledged, excludeAlarmIds, isForAlarmSound);
	};

	const headerColumnsWidth: CSSProperties = useMemo(() => {
		const retValue = getColumnWidth<AlarmTableDataSourceModel>(tableColumns, tableColumnsWidth as Record<keyof AlarmTableDataSourceModel, string>);
		const arr: string[] = retValue.gridTemplateColumns?.toString()?.split(' ');
		// since we have action fixed column, we need to replace penultimate column width to 1fr
		if (arr?.length >= 2) {
			arr.splice(-2, 1, `minmax(${arr.at(-2)}, 1fr)`);
			retValue.gridTemplateColumns = arr.join(' ');
		}
		return retValue;
	}, [
		tableColumns,
		iconViewColumnReference.current.notes,
		iconViewColumnReference.current.instruction,
		iconViewColumnReference.current.expand,
		iconViewColumnReference.current.notesAndInstructions,
	]);

	const headerBarConfigureButton: React.ReactNode = useMemo(
		() => (
			<Button
				id={`${isAcknowledgedAlarmTable ? 'acknowledged' : 'active'}AlarmTableConfigureButton`}
				type='text'
				icon={<SettingFilled />}
				onClick={() => setShowAlarmConfigureColumns(true)}
				disabled={!canChangeColumnConfiguration}
				title={getPermissionErrorMessage(canChangeColumnConfiguration)}
			/>
		),
		[]
	);

	const addOperatorNoteCallback = (action: AlarmAcknowledgementActions, payload: AcknowledgedAndClearAlarmsPayload) => {
		return getAlarmNoteAddOperatorNoteModal(action, true, payload.alarmIds, getDataSourceReference(), payload.alarmsDetails);
	};

	const openAddOperatorNoteModal = (action: AlarmAcknowledgementActions, isAlarmNoteForAcknowledge: boolean, alarmIds: React.Key[]) => {
		const alarmNote = getAlarmNoteAddOperatorNoteModal(action, isAlarmNoteForAcknowledge, alarmIds, getDataSourceReference());
		batch(() => {
			dispatch(setShowAlarmAddOperatorNote(true));
			dispatch(setAlarmNoteForAcknowledge(alarmNote));
		});
	};

	const getAllAlarmIds = async (): Promise<AlarmModel[]> => {
		const response = await alarmApi.getAllAlarmIds(isAcknowledgedAlarmTable);
		if (handleResponse(response)) {
			return [];
		}
		return response.Entity;
	};

	const setAlarmNoteForAcknowledgeCallback = (alarmNote: AlarmNoteForAcknowledge) => {
		dispatch(setAlarmNoteForAcknowledge(alarmNote));
	};

	const setShowAlarmAddOperatorNoteCallback = () => {
		dispatch(setShowAlarmAddOperatorNote(true));
	};

	const handleActionDropdown = async (option: AlarmAcknowledgementActions | AlarmActionOptions, record: AlarmTableDataSourceModel = undefined) => {
		let payload: AcknowledgedAndClearAlarmsPayload = {
			action: option as AlarmAcknowledgementActions,
			operatorNotes: [],
			alarmIds: [],
			acknowledgeAndClearCallback: acknowledgeAndClearAlarmCommon,
			addOperatorNoteCallback,
			setAlarmNoteForAcknowledgeCallback,
			setShowAlarmAddOperatorNoteCallback,
			setIsLoadingCallback: setIsLoading,
		};

		let actualSelectedAlarmKeys: React.Key[] = [];
		if (record) {
			actualSelectedAlarmKeys = record.AlarmDetails?.map(ad => ad.ActiveAlarmId) ?? [];
		} else {
			actualSelectedAlarmKeys = getSelectedAlarmKeysReference();
		}
		switch (option) {
			case AlarmAcknowledgementActions.AcknowledgedOne:
			case AlarmAcknowledgementActions.ClearOneAcknowledged:
			case AlarmAcknowledgementActions.ClearOneUnacknowledged: {
				payload.alarmIds = actualSelectedAlarmKeys.map<number>(x => Number(x));
				payload.action = getMultipleAlarmAction(actualSelectedAlarmKeys, option);
				acknowledgedAndClearAlarms(payload);
				break;
			}

			case AlarmAcknowledgementActions.AcknowledgedAll:
			case AlarmAcknowledgementActions.ClearAllAcknowledged:
			case AlarmAcknowledgementActions.ClearAllUnacknowledged: {
				setIsLoading(true);
				const alarmsModel: AlarmModel[] = await getAllAlarmIds().finally(() => setIsLoading(false));
				payload.alarmIds = selectAllAlarms(alarmsModel).map(x => Number(x));
				payload.alarmsDetails = alarmsModel;
				handleCheckboxMenuAction(CheckboxMenuOptions.SELECT_ALL);
				acknowledgedAndClearAlarms(payload);
				break;
			}

			case AlarmActionOptions.ADD_OPERATOR_NOTE: {
				const action = AlarmAcknowledgementActions.None;
				openAddOperatorNoteModal(action, false, actualSelectedAlarmKeys);
				break;
			}

			case AlarmActionOptions.DISPLAY_CREDENTIALS: {
				const actualDataSource = getDataSourceReference();
				const actualSelectedAlarmKeys = getSelectedAlarmKeysReference();
				const credentialsIds = [];

				actualSelectedAlarmKeys.forEach(x => {
					actualDataSource.forEach(y => {
						y.AlarmDetails.forEach(z => {
							if (z.ActiveAlarmId === Number(x)) {
								if (z.UID1 > 0) {
									credentialsIds.push(z.UID1);
								}

								if (z.UID2 > 0) {
									credentialsIds.push(z.UID2);
								}
							}
						});
					});
				});

				if (credentialsIds.length) {
					dispatch(
						setShowAlarmCredentials({
							showCredentials: true,
							credentialsIds,
						})
					);
				}
				break;
			}
		}
	};

	const actualDataSource = getDataSourceReference();
	const actualSelectedAlarmKeys = getSelectedAlarmKeysReference();
	const hasSelectedAlarms = actualSelectedAlarmKeys.length > 0;
	const applyBorder: string = actualDataSource.length === 0 ? styles.border : '';
	const prefixBody: string = isAcknowledgedAlarmTable ? 'acknowledgedAlarms' : 'alarms';

	const toggleItemActiveCallback = useCallback((itemIndex: number) => {
		toggleItemActive(itemIndex);
	}, []);

	const handleActionDropdownCallback = useCallback(async (option: AlarmAcknowledgementActions | AlarmActionOptions) => {
		handleActionDropdown(option);
	}, []);

	const initTableCallback = useCallback(
		async skipRender => {
			if (!skipRender) {
				initTable();
			}
		},
		[alarmColors]
	);

	const formatRowForClipboard = (alarm: AlarmTableDataSourceModel, visibleColumns: string[]): string => {
		let index = 0;
		const formattedRow = alarm.AlarmDetails.map(alarmDetail => {
			const isExpandableRow: boolean = (alarm.AlarmDetails.length > 1 && alarmStackingEnabled) || alarm.IsExpanded;
			return visibleColumns
				.map(column => {
					const text = getColumnValue({
						column,
						alarmDetail,
						data: alarm,
						isHeaderRow: isExpandableRow && index === 0,
						alwaysShowInstructions,
					}).toString();
					index++;
					return text;
				})
				.join('\t');
		}).join('\n');

		return formattedRow;
	};

	const handleCopyToClipboard = async (copyAction: AlarmTableContextAction): Promise<void> => {
		let text2Copy = '';

		const alarms = getDataSourceReference().filter(alarm => alarm.AlarmDetails[0].IsChecked);
		const visibleColumns = tableColumns
			.filter(tableColumn => tableColumn.title !== '' && !['Icon', 'ActionsColumn'].includes(tableColumn.dataIndex.toString()))
			.map(column => column.dataIndex.toString());

		if (copyAction === AlarmTableContextAction.CopyAddress) {
			text2Copy = alarms[0].Address;
		} else {
			if (copyAction === AlarmTableContextAction.CopyRowsWithHeaders) {
				text2Copy = visibleColumns.join('\t') + '\n';
			}
			text2Copy += alarms
				.map(alarm => {
					return formatRowForClipboard(alarm, visibleColumns);
				})
				.join('\n');
		}

		await navigator.clipboard.writeText(text2Copy);
	};

	const handleActionScope = async (key: number): Promise<void> => {
		switch (key) {
			case AlarmTableContextAction.CopyRows:
			case AlarmTableContextAction.CopyRowsWithHeaders:
			case AlarmTableContextAction.CopyAddress:
				await handleCopyToClipboard(key);
				break;

			case AlarmTableContextAction.DisplayCredential:
				handleActionDropdown(AlarmActionOptions.DISPLAY_CREDENTIALS);
				break;
		}
	};

	const onSort = (paginationSettings: EventPaginationSetting) => {
		if (window.sessionStorage.getItem(sessionStorageLiveEventKey) === '1' || window.sessionStorage.getItem(sessionStorageFetchKey) === '1') {
			return;
		}
		window.sessionStorage.setItem(sessionStorageFetchKey, '1');
		setIsLoading(true);
		setPaginationSettings(paginationSettings);
		setTotalAlarms(0);
		setSelectedAlarmKeysReference([]);
		alarmApi
			.getStoredAlarms({ ...paginationSettings }, isAcknowledgedAlarmTable, [], false)
			.then(response => {
				processGetStoredAlarmResponse(response, [], 0, paginationSettings);
			})
			.catch(err => {
				Logger.writeErrorLog(err);
			})
			.finally(() => {
				setIsLoading(false);
				window.sessionStorage.setItem(sessionStorageFetchKey, '0');
			});
	};

	const doSelectedAlarmsHaveSameAddress = (): boolean => {
		let sameAddress = true;
		const alarms = getDataSourceReference()?.filter(alarm => alarm.AlarmDetails[0].IsChecked);
		if (alarms?.length > 1) {
			const addressReference = alarms[0].Address;
			if (alarms.splice(1).some(alarm => alarm.Address !== addressReference)) {
				sameAddress = false;
			}
		}

		return sameAddress;
	};

	const setShowAlarmConfigureFalseColumnsCallback = useCallback(() => setShowAlarmConfigureColumns(false), []);
	const backgroundColorStyles: CSSProperties = getAlarmBackgroundColor(alarmColors);

	const items = createContextMenuOptions();
	const contextMenuOptions: SelectOptions<number>[] = [];
	const buildContextMenuOptions = () => {
		const isMultipleSameAddress = doSelectedAlarmsHaveSameAddress();
		const newItems = [
			...items?.options,
			{
				id: 'alarmContextMenuOptions-CopyAddress',
				key: `displayBadges-${AlarmTableContextAction.CopyAddress}`,
				label: 'Copy Address',
				value: AlarmTableContextAction.CopyAddress,
				disabled: !isMultipleSameAddress,
				title: getPermissionErrorMessage(true, false, !isMultipleSameAddress),
			},
		];

		return newItems;
	};

	contextMenuOptions.push(...buildContextMenuOptions());

	return (
		<>
			<div id={`${prefixBody}Container`} className={styles.container}>
				<div id={`${prefixBody}Actions`} className={styles.alarmActionsContainer}>
					<Space size={10}>
						<IndividualActionButtons
							handleAction={handleActionDropdownCallback}
							isAcknowledgedAlarm={isAcknowledgedAlarmTable}
							isAlarmSelected={hasSelectedAlarms}
						/>
						<AlarmActionDropdown
							isAlarmTableEmpty={actualDataSource.length === 0}
							isAcknowledgedAlarmTable={isAcknowledgedAlarmTable}
							handleActionDropdown={handleActionDropdownCallback}
						/>
					</Space>
				</div>
				<div id={`${prefixBody}TableContainer`} className={applyBorder}>
					<HeaderBar
						title={isAcknowledgedAlarmTable ? _('AcknowledgedAlarms') : _('Alarms')}
						buttons={
							<div>
								<HelpFactoryComponent helpFeature={HelpFeature.Alarms} topicId={topicId} size='small' />
								{headerBarConfigureButton}
							</div>
						}
					/>
					<div className={styles.alarmContainer}>
						{actualDataSource.length === 0 ? (
							<div className={styles.empty}>
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description={isAcknowledgedAlarmTable ? _('ThereAreNoAcknowledgedAlarms') : _('ThereAreNoActiveAlarms')}
								/>
							</div>
						) : (
							<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
								<VirtualTableExpandableRow
									columns={tableColumns}
									dataSource={actualDataSource}
									fetchData={fetchData}
									pageSize={alarmsPerPage}
									headerColumnsWidth={headerColumnsWidth}
									maxHeight={maxHeight}
									idTable={`${prefixBody}Table`}
									handleOnExpandRow={toggleItemActiveCallback}
									alwaysShowInstructions={alwaysShowInstructions}
									alarmStackingEnabled={alarmStackingEnabled}
									totalAlarms={totalActiveAlarms}
									sessionStorageFetchKey={sessionStorageFetchKey}
									sessionStorageLiveEventKey={sessionStorageLiveEventKey}
									containerClassName={isAcknowledgedAlarmTable ? 'acknowledgedContainer' : 'activeContainer'}
									customStyles={backgroundColorStyles}
									contextMenuOptions={contextMenuOptions}
									onClickContextMenuOption={key => {
										handleActionScope(key);
									}}
									onRightClickRow={(checked, activeAlarmId, isExpandableRowHeader, isExpanded) => {
										onChangeAlarmCheckbox(checked, activeAlarmId, isExpandableRowHeader, isExpanded, true, true);
									}}
									paginationSettings={paginationSettings}
									onSort={onSort}
								/>
							</Spin>
						)}
						{totalAlarms !== 0 && (
							<div className={styles.tableCount}>
								{actualSelectedAlarmKeys.length > 0 ? (
									<span>{`${actualSelectedAlarmKeys.length}/${totalAlarms} ${_('Items')}`}</span>
								) : (
									<span>{`${totalAlarms} ${_('Items')}`}</span>
								)}
							</div>
						)}
					</div>
				</div>
				{showAlarmConfigureColumns && (
					<AlarmViewerProperties
						onHideModal={setShowAlarmConfigureFalseColumnsCallback}
						dispatchAction={initTableCallback}
						isAcknowledgedAlarmTable={isAcknowledgedAlarmTable}
					/>
				)}
			</div>
		</>
	);
};

const AlarmTable = React.memo(component);
AlarmTable.displayName = 'AlarmTable';

export { AlarmTable };
